import React, { useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react'
import './CreateRentalContractPage.css'
import httpClient from '../../components/helpers/httpClient'
import PopupDropdown from '../../components/PopupDropdown'
import { DateField } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { NumericFormat } from 'react-number-format'
import CheckBox from '../../components/CheckBox'
import { rentalContractTypeLabelColors, rentalContractTypes, epcLabelOptions } from '../../assets/data/data'
import { formatDate, parseDateValue, serializeDate } from '../../components/helpers/formatDate'

const RENTAL_TYPES = rentalContractTypes
const CONTRACT_TYPES = ["1 jaar", "1 jaar - student", "3/6/9", "onbepaalde duur"]
const PROVISION_TYPES = ["forfait", "provision"]

const getCalculatedEndDate = (contractType, startDate) => {
  const autoCalcTypes = ['1 jaar', '1 jaar - student', '3/6/9']
  if (!autoCalcTypes.includes(contractType) || !startDate) return null

  const start = dayjs(startDate)
  if (!start.isValid()) return null

  const yearsToAdd = ['1 jaar', '1 jaar - student'].includes(contractType) ? 1 : 9
  return start.add(yearsToAdd, 'year').format('YYYY-MM-DD')
}

const getNext369BreakDate = (contractType, startDate) => {
  if (contractType !== '3/6/9' || !startDate) return null

  const start = dayjs(startDate)
  if (!start.isValid()) return null

  const today = dayjs().startOf('day')
  for (const years of [3, 6]) {
    const breakDate = start.add(years, 'year')
    if (breakDate.isSame(today) || breakDate.isAfter(today)) {
      return breakDate.format('YYYY-MM-DD')
    }
  }

  return null
}

const formatDisplayDate = (value) => {
  return formatDate(value)?.replaceAll('/', '-') || 'N/A'
}

const emptyForm = {
  rental_type: "",
  active: true,
  contract_type: "",
  signing_date: null,
  start_date: null,
  end_date: null,
  start_rental_price: "",
  start_rental_price_year: 0,
  provision_price: "",
  provision_type: "",
  can_be_indexed: true,
  indexed_rental_price: "",
  indexed_rental_price_year: 0,
  guarantee_type: "",
  guarantee_amount: "",
  guarantee_ok: false,
  registration_done: false,
  insurance: "",
  notes: "",
}

export const RentalContractForm = forwardRef(function RentalContractForm(
  {
    initialData = null,
    readOnly = false,
    onSubmit,
    // When true, this component renders 3 separate `body__section`s (DETAILS / DURATION / PRICE)
    // so the detail page can look more like ContactDetailPage.
    splitSections = false,
    expandedSection = null,
    onToggleSection = null,
  },
  ref
) {
  const [contacts, setContacts] = useState([])
  const [companies, setCompanies] = useState([])
  const [unitsOptions, setUnitsOptions] = useState([])
  const [selectedUnits, setSelectedUnits] = useState([])

  const [form, setForm] = useState(emptyForm)
  const [tenants, setTenants] = useState([])
  const [owners, setOwners] = useState([])
  const [subtenants, setSubtenants] = useState([])

  // Initialize from initialData
  useEffect(() => {
    if (!initialData) return

    const normalizeDate = (value) => {
      return serializeDate(value)
    }

    const normalizedSigningDate = normalizeDate(initialData.signing_date)
    const normalizedStartDate = normalizeDate(initialData.start_date)
    const normalizedEndDate = normalizeDate(initialData.end_date)
    const calculatedEndDate = getCalculatedEndDate(initialData.contract_type || "", normalizedStartDate)

    setForm(prev => ({
      ...prev,
      rental_type: initialData.rental_type || "",
      active: initialData.active ?? true,
      contract_type: initialData.contract_type || "",
      signing_date: normalizedSigningDate,
      start_date: normalizedStartDate,
      end_date: normalizedEndDate || calculatedEndDate,
      start_rental_price: initialData.start_rental_price ?? "",
      provision_price: initialData.provision_price ?? "",
      provision_type: initialData.provision_type || "",
      can_be_indexed: initialData.can_be_indexed ?? true,
      indexed_rental_price: initialData.indexed_rental_price ?? "",
      start_rental_price_year: Math.round((Number(initialData.start_rental_price) || 0) * 12 * 100) / 100,
      indexed_rental_price_year: Math.round((Number(initialData.indexed_rental_price) || 0) * 12 * 100) / 100,
      guarantee_type: initialData.guarantee_type ?? "",
      guarantee_amount: initialData.guarantee_amount ?? "",
      guarantee_ok: initialData.guarantee_ok ?? false,
      registration_done: initialData.registration_done ?? false,
      insurance: initialData.insurance ?? "",
      notes: initialData.notes ?? "",
    }))

    setSelectedUnits((initialData.units || []).map(u => ({
      id: u.id,
      unit_name: u.unit_name,
      project_name: u.building_id?.project_id?.project_commercial_name || u.building_id?.project_id?.project_placeholder_name,
      EPC_label: u.EPC_label,
    })))

    const participants = initialData.participants || []
    const toItem = p => p.company ? { ...p.company, id: p.company.id, type: 'company' } : { ...p.contact, id: p.contact.id, type: 'contact' }
    setTenants(participants.filter(p => p.participant_type === 'tenant' && (p.company || p.contact)).map(toItem))
    setOwners(participants.filter(p => p.participant_type === 'landlord' && (p.company || p.contact)).map(toItem))
    setSubtenants(participants.filter(p => p.participant_type === 'subtenant' && (p.company || p.contact)).map(toItem))
  }, [initialData])

  useEffect(() => {
    fetchContacts(); fetchCompanies(); fetchUnits()
  }, [])

  const fetchContacts = async () => {
    try { const r = await httpClient.get(import.meta.env.VITE_API_URL + `/api/contacts/get`); if (r.status === 200) setContacts(r.data) } catch { }
  }
  const fetchCompanies = async () => {
    try { const r = await httpClient.get(import.meta.env.VITE_API_URL + `/api/companies/get?type=limited-info`); if (r.status === 200) setCompanies(r.data) } catch { }
  }
  const fetchUnits = async () => {
    try {
      const r = await httpClient.post(import.meta.env.VITE_API_URL + "/api/units/get", { filters: null, search: null })
      if (r.status === 200) {
        const units = r.data.map(unit => ({ id: unit.id, unit_name: unit.unit_name, project_name: unit.building_id.project_id.project_commercial_name || unit.building_id.project_id.project_placeholder_name, EPC_label: unit.EPC_label }))
        setUnitsOptions(units)
      }
    } catch { }
  }

  // Derived yearly values
  useEffect(() => {
    const monthly = Number(form.start_rental_price) || 0
    const indexedMonthly = Number(form.indexed_rental_price) || 0
    setForm(prev => ({
      ...prev,
      start_rental_price_year: Math.round(monthly * 12 * 100) / 100,
      indexed_rental_price_year: Math.round(indexedMonthly * 12 * 100) / 100,
    }))
  }, [form.start_rental_price, form.indexed_rental_price])

  const handleEndDateChange = (value) => {
    const formatted = serializeDate(value)
    setForm(prev => ({ ...prev, end_date: formatted }))
  }

  const handleContractTypeChange = (val) => {
    const previousCalculatedEndDate = getCalculatedEndDate(form.contract_type, form.start_date)
    const shouldUseCalculatedEndDate = !form.end_date || form.end_date === previousCalculatedEndDate
    const calculatedEndDate = getCalculatedEndDate(val, form.start_date)
    setForm(prev => {
      return {
        ...prev,
        contract_type: val,
        end_date: shouldUseCalculatedEndDate ? calculatedEndDate : prev.end_date,
      }
    })
  }

  const handleStartDateChange = (value) => {
    const formatted = serializeDate(value)
    const previousCalculatedEndDate = getCalculatedEndDate(form.contract_type, form.start_date)
    const shouldUseCalculatedEndDate = !form.end_date || form.end_date === previousCalculatedEndDate
    const calculatedEndDate = getCalculatedEndDate(form.contract_type, formatted)
    setForm(prev => {
      if (shouldUseCalculatedEndDate && !formatted && !calculatedEndDate && prev.contract_type !== 'onbepaalde duur') {
        return { ...prev, start_date: formatted, end_date: null }
      }
      return {
        ...prev,
        start_date: formatted,
        end_date: shouldUseCalculatedEndDate ? calculatedEndDate : prev.end_date,
      }
    })
  }

  const participantOptions = useMemo(() => ([
    ...contacts.map(item => ({ ...item, type: 'contact' })),
    ...companies.map(item => ({ ...item, type: 'company' }))
  ]), [contacts, companies])

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleDateChange = (field, value) => {
    const formatted = serializeDate(value)
    setForm(prev => ({ ...prev, [field]: formatted }))
  }

  const epcScore = useMemo(() => {
    const labels = (selectedUnits || []).map(u => u?.EPC_label).filter(Boolean)
    const unique = Array.from(new Set(labels))
    unique.sort((a, b) => {
      const ia = epcLabelOptions.indexOf(a)
      const ib = epcLabelOptions.indexOf(b)
      if (ia === -1 && ib === -1) return a.localeCompare(b)
      if (ia === -1) return 1
      if (ib === -1) return -1
      return ia - ib
    })
    return unique.join(', ')
  }, [selectedUnits])

  const next369BreakDate = useMemo(() => (
    getNext369BreakDate(form.contract_type, form.start_date)
  ), [form.contract_type, form.start_date])

  const buildParticipants = () => {
    const toParticipant = (arr, participant_type) => (arr || []).map(p => ({
      contact_id: p.type === 'contact' ? p.id : null,
      company_id: p.type === 'company' ? p.id : null,
      participant_type,
    }))
    return [
      ...toParticipant(tenants, 'tenant'),
      ...toParticipant(owners, 'landlord'),
      ...toParticipant(subtenants, 'subtenant'),
    ]
  }

  useImperativeHandle(ref, () => ({
    async submit() {
      const payload = {
        rental_type: form.rental_type,
        active: form.active,
        contract_type: form.contract_type,
        signing_date: form.signing_date,
        start_date: form.start_date,
        end_date: form.end_date,
        start_rental_price: form.start_rental_price !== '' ? Number(form.start_rental_price) : null,
        provision_price: form.provision_price !== '' ? Number(form.provision_price) : null,
        provision_type: form.provision_type || null,
        can_be_indexed: form.can_be_indexed,
        indexed_rental_price: form.indexed_rental_price !== '' ? Number(form.indexed_rental_price) : null,
        guarantee_type: form.guarantee_type || null,
        guarantee_amount: form.guarantee_amount !== '' ? Number(form.guarantee_amount) : null,
        guarantee_ok: !!form.guarantee_ok,
        registration_done: !!form.registration_done,
        insurance: form.insurance || null,
        notes: form.notes || null,
        unit_ids: (selectedUnits || []).map(u => u.id),
        participants: buildParticipants(),
      }
      if (onSubmit) await onSubmit(payload)
    }
  }))

  const renderDetailsFields = () => (
    <>
      <div className='row-2'>
        <div className="input_field--combo-grid">
          <label className="input_field__label">Rental type</label>
          <PopupDropdown
            options={RENTAL_TYPES}
            value={form.rental_type}
            onChange={(val) => !readOnly && handleChange('rental_type', val)}
            title={'Select type'}
            searchBar={false}
            labelView={true}
            labelColors={rentalContractTypeLabelColors}
            disabled={readOnly}
          />
        </div>
        <div className="row-2">
          <div className="input_field--combo-grid no-border align-right">
            <label className="input_field__label">Active</label>
            <CheckBox
              state={form.active}
              setState={() => !readOnly && setForm(prev => ({ ...prev, active: !prev.active }))}
              height="30px"
              margin="5px"
              marginLeft="auto"
              alignRight={true}
            />
          </div>
          <div className="input_field--combo-grid no-border align-right">
            <label className="input_field__label">Registration done</label>
            <CheckBox
              state={!!form.registration_done}
              setState={() => !readOnly && setForm(prev => ({ ...prev, registration_done: !prev.registration_done }))}
              height="30px"
              margin="5px"
              marginLeft="auto"
              alignRight={true}
            />
          </div>
        </div>
      </div>

      <div className='row-2'>
        <div className="input_field--combo-grid">
          <label className="input_field__label">Unit(s)</label>
          <div style={readOnly ? { pointerEvents: 'none', opacity: .8 } : undefined}>
            <PopupDropdown
              searchBar={true}
              options={unitsOptions}
              value={selectedUnits}
              onChange={setSelectedUnits}
              title={"Select units"}
              multiselect={true}
              width="520px"
              excludeOptions={['EPC_label']}
              disabled={readOnly}
            />
          </div>
        </div>
        <div className="input_field--combo-grid">
          <label className="input_field__label">EPC scores</label>
          <input className="input_field__input_box--text" value={epcScore || 'N/A'} readOnly />
        </div>
      </div>

      <div className='row-1'>
        <div className="input_field--combo-grid">
          <label className="input_field__label">Owners</label>
          <div style={readOnly ? { pointerEvents: 'none', opacity: .8 } : undefined}>
            <PopupDropdown
              searchBar={true}
              options={participantOptions}
              value={owners}
              onChange={setOwners}
              title={"Select owners"}
              multiselect={true}
              width="520px"
              disabled={readOnly}
            />
          </div>
        </div>
      </div>

      <div className='row-1'>
        <div className="input_field--combo-grid">
          <label className="input_field__label">Tenants</label>
          <div style={readOnly ? { pointerEvents: 'none', opacity: .8 } : undefined}>
            <PopupDropdown
              searchBar={true}
              options={participantOptions}
              value={tenants}
              onChange={setTenants}
              title={"Select tenants"}
              multiselect={true}
              width="520px"
              disabled={readOnly}
            />
          </div>
        </div>
      </div>
      <div className='row-1'>
        <div className="input_field--combo-grid">
          <label className="input_field__label">Subtenants</label>
          <div style={readOnly ? { pointerEvents: 'none', opacity: .8 } : undefined}>
            <PopupDropdown
              searchBar={true}
              options={participantOptions}
              value={subtenants}
              onChange={setSubtenants}
              title={"Select subtenants"}
              multiselect={true}
              width={"520px"}
              disabled={readOnly}
            />
          </div>
        </div>
      </div>
    </>
  )

  const renderDurationFields = () => (
    <>
      <div className='row-1'>

        <div className="input_field--combo-grid">
          <label className="input_field__label">Contract type</label>
          <PopupDropdown
            options={CONTRACT_TYPES}
            value={form.contract_type}
            onChange={(val) => !readOnly && handleContractTypeChange(val)}
            title={'Select contract type'}
            searchBar={false}
            disabled={readOnly}
          />
        </div>
      </div>

      <div className='row-1'>
        <div className="input_field--combo-grid">
          <label className="input_field__label">Signing date</label>
          <DateField
            className="date-picker"
            format="DD-MM-YYYY"
            value={parseDateValue(form.signing_date)}
            onChange={(val) => !readOnly && handleDateChange('signing_date', val)}
            readOnly={readOnly}
            disabled={readOnly}
          />
        </div>
      </div>
      <div className='row-1'>
        <div className="input_field--combo-grid">
          <label className="input_field__label">Start date</label>
          <DateField
            className="date-picker"
            format="DD-MM-YYYY"
            value={parseDateValue(form.start_date)}
            onChange={(val) => !readOnly && handleStartDateChange(val)}
            readOnly={readOnly}
            disabled={readOnly}
          />
        </div>
      </div>
      <div className='row-1'>
        <div className="input_field--combo-grid">
          <label className="input_field__label">End date</label>
          <DateField
            className="date-picker"
            format="DD-MM-YYYY"
            value={parseDateValue(form.end_date)}
            onChange={(val) => !readOnly && handleEndDateChange(val)}
            readOnly={readOnly}
            disabled={readOnly}
          />
        </div>
      </div>
      {form.contract_type === '3/6/9' && (
        <div className='row-2'>
          <div className="input_field--combo-grid">
            <label className="input_field__label">Next owner break</label>
            <input
              className="input_field__input_box--text"
              value={formatDisplayDate(next369BreakDate)}
              readOnly
            />
          </div>
          <div className="input_field--combo-grid">
            <label className="input_field__label">Next tenant break</label>
            <input
              className="input_field__input_box--text"
              value={formatDisplayDate(next369BreakDate)}
              readOnly
            />
          </div>
        </div>
      )}
    </>
  )

  const renderPriceFields = () => (
    <>
      <div className='row-2'>
        <div className="input_field--combo-grid">
          <label className="input_field__label">Start price <br />(month)</label>
          <div className="input-with-euro">
            <span className="euro-sign">€</span>
            <NumericFormat
              className="input_field__input_box--text"
              value={form.start_rental_price}
              thousandSeparator=" "
              decimalSeparator=","
              decimalScale={2}
              allowNegative={false}
              onValueChange={(values) => !readOnly && handleChange('start_rental_price', values.floatValue ?? '')}
              readOnly={readOnly}
              displayType="input"
            />
          </div>
        </div>
        <div className="input_field--combo-grid">
          <label className="input_field__label">Start price <br />(year)</label>
          <div className="input-with-euro">
            <span className="euro-sign">€</span>
            <NumericFormat
              className="input_field__input_box--text"
              value={form.start_rental_price_year}
              thousandSeparator=" "
              decimalSeparator=","
              decimalScale={2}
              fixedDecimalScale={true}
              displayType="input"
              readOnly
            />
          </div>
        </div>
      </div>

      <div className='row-1'>
        <div className="input_field--combo-grid">
          <label className="input_field__label">Provision <br />(month)</label>
          <div className="input-with-euro">
            <span className="euro-sign">€</span>
            <NumericFormat
              className="input_field__input_box--text"
              value={form.provision_price}
              thousandSeparator=" "
              decimalSeparator=","
              decimalScale={2}
              allowNegative={false}
              onValueChange={(values) => !readOnly && handleChange('provision_price', values.floatValue ?? '')}
              readOnly={readOnly}
              displayType="input"
            />
          </div>
        </div>
      </div>
      <div className='row-1'>
        <div className="input_field--combo-grid">
          <label className="input_field__label">Provision type</label>
          <PopupDropdown
            options={PROVISION_TYPES}
            value={form.provision_type}
            onChange={(val) => !readOnly && handleChange('provision_type', val)}
            title={'Select type'}
            searchBar={false}
            disabled={readOnly}
          />
        </div>
      </div>

      <div className='row-1'>
        <div className="input_field--combo-grid">
          <label className="input_field__label">Can be indexed</label>
          <CheckBox
            state={form.can_be_indexed}
            setState={() => !readOnly && setForm(prev => ({ ...prev, can_be_indexed: !prev.can_be_indexed }))}
            height="30px"
            margin="5px"
          />
        </div>
      </div>
      <div className='row-1'>
        <div className="input_field--combo-grid">
          <label className="input_field__label">Indexed price <br />(month)</label>
          <div className="input-with-euro">
            <span className="euro-sign">€</span>
            <NumericFormat
              className="input_field__input_box--text"
              value={form.indexed_rental_price}
              thousandSeparator=" "
              decimalSeparator=","
              decimalScale={2}
              allowNegative={false}
              onValueChange={(values) => !readOnly && handleChange('indexed_rental_price', values.floatValue ?? '')}
              readOnly={readOnly}
              displayType="input"
            />
          </div>
        </div>
        <div className="input_field--combo-grid">
          <label className="input_field__label">Indexed price <br />(year)</label>
          <div className="input-with-euro">
            <span className="euro-sign">€</span>
            <NumericFormat
              className="input_field__input_box--text"
              value={form.indexed_rental_price_year}
              thousandSeparator=" "
              decimalSeparator=","
              decimalScale={2}
              fixedDecimalScale={true}
              displayType="input"
              readOnly
            />
          </div>
        </div>
      </div>
    </>
  )

  const renderGuaranteeFields = () => (
    <>
      <div className='row-1'>
        <div className="input_field--combo-grid">
          <label className="input_field__label">Guarantee type</label>
          <input
            className="input_field__input_box--text"
            value={form.guarantee_type || ''}
            onChange={(e) => !readOnly && handleChange('guarantee_type', e.target.value)}
            readOnly={readOnly}
          />
        </div>
      </div>
      <div className='row-1'>
        <div className="input_field--combo-grid">
          <label className="input_field__label">Guarantee amount</label>
          <div className="input-with-euro">
            <span className="euro-sign">€</span>
            <NumericFormat
              className="input_field__input_box--text"
              value={form.guarantee_amount}
              thousandSeparator=" "
              decimalSeparator=","
              decimalScale={2}
              allowNegative={false}
              onValueChange={(values) => !readOnly && handleChange('guarantee_amount', values.floatValue ?? '')}
              readOnly={readOnly}
              displayType="input"
            />
          </div>
        </div>
      </div>
      <div className='row-1'>
        <div className="input_field--combo-grid">
          <label className="input_field__label">Insurance</label>
          <input
            className="input_field__input_box--text"
            value={form.insurance || ''}
            onChange={(e) => !readOnly && handleChange('insurance', e.target.value)}
            readOnly={readOnly}
          />
        </div>
      </div>
      <div className='row-1'>
        <div className="input_field--combo-grid no-border align-right">
          <label className="input_field__label">Guarantee ok</label>
          <CheckBox
            state={!!form.guarantee_ok}
            setState={() => !readOnly && setForm(prev => ({ ...prev, guarantee_ok: !prev.guarantee_ok }))}
            height="30px"
            margin="5px"
            marginLeft="auto"
            alignRight={true}
          />
        </div>
      </div>
    </>
  )

  const renderNotesFields = () => (
    <>
      <textarea
        className="input_field__input_box--text-area"
        value={form.notes || ''}
        onChange={(e) => !readOnly && handleChange('notes', e.target.value)}
        readOnly={readOnly}
        rows={10}
      />
    </>
  )

  const panelClassName = `rental-contract-form panel panel--form ${readOnly ? 'read-only' : ''}`

  if (splitSections) {
    return (
      <>
        <div
          className={`body__section ${expandedSection === "rental-contract-details" ? "full-screen" : ""}`}
          id="rental-contract-details"
        >
          <h2 onClick={() => onToggleSection?.("rental-contract-details")}>DETAILS</h2>
          <div className="form__section">
            <div className={panelClassName}>
              {renderDetailsFields()}
            </div>
          </div>
        </div>

        <div
          className={`body__section ${expandedSection === "rental-contract-guarantee" ? "full-screen" : ""}`}
          id="rental-contract-guarantee"
        >
          <h2 onClick={() => onToggleSection?.("rental-contract-guarantee")}>GUARANTEE</h2>
          <div className="form__section">
            <div className={panelClassName}>
              {renderGuaranteeFields()}
            </div>
          </div>
        </div>

        <div
          className={`body__section ${expandedSection === "rental-contract-duration" ? "full-screen" : ""}`}
          id="rental-contract-duration"
        >
          <h2 onClick={() => onToggleSection?.("rental-contract-duration")}>DURATION</h2>
          <div className="form__section">
            <div className={panelClassName}>
              {renderDurationFields()}
            </div>
          </div>
        </div>

        <div
          className={`body__section ${expandedSection === "rental-contract-price" ? "full-screen" : ""}`}
          id="rental-contract-price"
        >
          <h2 onClick={() => onToggleSection?.("rental-contract-price")}>PRICE</h2>
          <div className="form__section">
            <div className={panelClassName}>
              {renderPriceFields()}
            </div>
          </div>
        </div>

        <div
          className={`body__section ${expandedSection === "rental-contract-notes" ? "full-screen" : ""}`}
          id="rental-contract-notes"
        >
          <h2 onClick={() => onToggleSection?.("rental-contract-notes")}>NOTES</h2>
          <div className="form__section">
            <div className={panelClassName}>
              {renderNotesFields()}
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <div className={panelClassName}>
      {renderDetailsFields()}

      <div className='subtitle'>Duration</div>
      {renderDurationFields()}

      <div className='subtitle'>Price</div>
      {renderPriceFields()}

      <div className='subtitle'>Guarantee</div>
      {renderGuaranteeFields()}

      <div className='subtitle'>Notes</div>
      {renderNotesFields()}
    </div>
  )
})

export default RentalContractForm


